import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardBody,
  MetricCard,
  InsightCard,
  DataTable,
  Chart,
  DashboardSkeleton,
  PageError,
  SampleDataBanner,
} from "../components/index.js";
import { useDashboardData } from "../hooks/useDashboardData.js";
import { rangeDays } from "../lib/time.js";

export function Traffic() {
  const { data, loading, error, refetch } = useDashboardData("traffic");
  const [searchParams] = useSearchParams();
  const days = rangeDays(searchParams.get("from"), searchParams.get("to"));

  if (loading) return <DashboardSkeleton />;
  if (error)   return <PageError error={error} onRetry={refetch} />;
  if (!data)   return null;

  const { metrics, cities, landingPages, hourlyTraffic, notableSources, insights, period } = data;

  return (
    <div className="space-y-5 max-w-[1400px]">
      {period?.hasRealData === false && <SampleDataBanner />}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard {...metrics.direct} />
        <MetricCard {...metrics.organic} />
        <MetricCard {...metrics.ads} />
        <MetricCard {...metrics.other} />
      </div>

      <Card>
        <CardHeader
          title="Hourly Traffic"
          subtitle={`Sessions by hour of day · ${days}-day average`}
        />
        <CardBody>
          <Chart
            type="bar"
            data={hourlyTraffic}
            xKey="hour"
            series={[{ key: "sessions", color: "#ff6804", label: "Sessions" }]}
            height={220}
          />
        </CardBody>
      </Card>

      {insights?.[0] && <InsightCard>{insights[0].content}</InsightCard>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader title="Top Cities" subtitle="Sessions by geography" />
          <DataTable
            columns={[
              { key: "city",     label: "City" },
              { key: "sessions", label: "Sessions", align: "right",
                render: (r) => r.sessions.toLocaleString() },
            ]}
            rows={cities}
          />
        </Card>

        <Card>
          <CardHeader title="Landing Pages" subtitle={`Entry pages · ${days} days`} />
          <DataTable
            columns={[
              { key: "page",     label: "Page" },
              { key: "sessions", label: "Sessions", align: "right",
                render: (r) => r.sessions.toLocaleString() },
              { key: "bounce",   label: "Bounce %", align: "right",
                render: (r) => `${r.bounce}%`,
                colorFn: (r) => (r.bounce > 15 ? "#dc2626" : undefined) },
            ]}
            rows={landingPages}
          />
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Notable Sources"
          subtitle="Emerging and underappreciated channels"
        />
        <DataTable
          columns={[
            { key: "source",   label: "Source" },
            { key: "sessions", label: "Sessions", align: "right",
              render: (r) => r.sessions.toLocaleString() },
            { key: "note",     label: "Analysis" },
          ]}
          rows={notableSources}
          highlightFn={(r) => r.source.includes("AI") || r.source === "Perplexity"}
        />
      </Card>

      {insights?.[1] && <InsightCard>{insights[1].content}</InsightCard>}
    </div>
  );
}
